import { apiUrl } from './api-url';

export type PushPromptVariant =
  | 'A_likes_blur'
  | 'B_first_match'
  | 'C_exit_intent'
  | 'D_welcome'
  | 'E_first_message'
  | 'F_first_swipe';
export type PushPromptEventType = 'shown' | 'accepted' | 'dismissed' | 'declined_permission';
export type PushPromptPlatform = 'ios_pwa' | 'android_pwa' | 'desktop' | 'mobile_web';

export function detectPlatform(): PushPromptPlatform {
  if (typeof window === 'undefined') return 'desktop';
  const ua = navigator.userAgent || '';
  const isIOS = /iphone|ipad|ipod/i.test(ua);
  const isAndroid = /android/i.test(ua);
  const standalone =
    window.matchMedia?.('(display-mode: standalone)').matches ||
    ('standalone' in navigator && (navigator as { standalone?: boolean }).standalone === true);
  if (isIOS) return standalone ? 'ios_pwa' : 'mobile_web';
  if (isAndroid) return standalone ? 'android_pwa' : 'mobile_web';
  return 'desktop';
}

export function isInstalledPWA(): boolean {
  if (typeof window === 'undefined') return false;
  if (window.matchMedia?.('(display-mode: standalone)').matches) return true;
  if ('standalone' in navigator && (navigator as { standalone?: boolean }).standalone === true) return true;
  return false;
}

interface QueueItem {
  variant: PushPromptVariant;
  eventType: PushPromptEventType;
  platform: PushPromptPlatform;
  isInstalled: boolean;
  contextMetadata?: Record<string, unknown> | null;
  ts: number;
}

const QUEUE_KEY = 'papacito_push_prompt_event_queue';

function readQueue(): QueueItem[] {
  try {
    const raw = localStorage.getItem(QUEUE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch { return []; }
}

function writeQueue(items: QueueItem[]) {
  try { localStorage.setItem(QUEUE_KEY, JSON.stringify(items.slice(-50))); } catch { /* ignore */ }
}

async function postEvent(token: string, item: QueueItem): Promise<boolean> {
  try {
    // 2026-05-01 Track 2: always inject navigator.userAgent into contextMetadata
    // so we can identify which browsers (Discord WebView, Telegram, Snapchat
    // configs we missed, etc.) are firing `no_notification_api`. Spread BEFORE
    // caller-supplied metadata so callers can override if they ever need to.
    const ua = typeof navigator !== 'undefined' ? navigator.userAgent : null;
    const callerMeta = item.contextMetadata ?? {};
    const mergedContextMetadata: Record<string, unknown> = {
      userAgent: ua,
      ...callerMeta,
    };
    const res = await fetch(apiUrl('/api/push/prompt-event'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        variant: item.variant,
        eventType: item.eventType,
        platform: item.platform,
        isInstalled: item.isInstalled,
        contextMetadata: mergedContextMetadata,
      }),
    });
    return res.ok;
  } catch {
    return false;
  }
}

export async function flushPromptEventQueue(token: string | null | undefined): Promise<void> {
  if (!token) return;
  const queue = readQueue();
  if (queue.length === 0) return;
  const remaining: QueueItem[] = [];
  for (const item of queue) {
    const ok = await postEvent(token, item);
    if (!ok) remaining.push(item);
  }
  writeQueue(remaining);
}

export async function logPromptEvent(
  token: string | null | undefined,
  variant: PushPromptVariant,
  eventType: PushPromptEventType,
  contextMetadata?: Record<string, unknown> | null,
): Promise<void> {
  const item: QueueItem = {
    variant,
    eventType,
    platform: detectPlatform(),
    isInstalled: isInstalledPWA(),
    contextMetadata: contextMetadata ?? null,
    ts: Date.now(),
  };
  if (!token || (typeof navigator !== 'undefined' && !navigator.onLine)) {
    const queue = readQueue();
    queue.push(item);
    writeQueue(queue);
    return;
  }
  const ok = await postEvent(token, item);
  if (!ok) {
    const queue = readQueue();
    queue.push(item);
    writeQueue(queue);
  }
}

export interface BehavioralContext {
  unseenLikesCount: number;
  profileViewsToday: number;
  totalMatches: number;
  hasPushSubscription: boolean;
  lastShownByVariant: Record<PushPromptVariant, string | null>;
  lastDeclinedAt: string | null;
}

export async function fetchBehavioralContext(token: string): Promise<BehavioralContext | null> {
  try {
    const res = await fetch(apiUrl('/api/push/behavioral-context'), {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) return null;
    return (await res.json()) as BehavioralContext;
  } catch {
    return null;
  }
}

if (typeof window !== 'undefined') {
  window.addEventListener('online', () => {
    try {
      const tokenRaw = localStorage.getItem('papacito_token') || sessionStorage.getItem('papacito_token');
      if (tokenRaw) flushPromptEventQueue(tokenRaw);
    } catch { /* ignore */ }
  });
}
